import { Component, ChangeDetectorRef, OnInit, OnDestroy, } from '@angular/core';
import { CommonModule } from '@angular/common';
import { HttpClient, HttpHeaders, HttpClientModule } from "@angular/common/http";
import { ToastModule } from 'primeng/toast';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { FormsModule } from '@angular/forms';
import { DropdownModule } from 'primeng/dropdown';
import { AutoCompleteModule } from "primeng/autocomplete";
import { ConfirmationService } from 'primeng/api';
import { DialogModule } from 'primeng/dialog';
import { Subscription, Subject } from 'rxjs';
import { distinctUntilChanged, debounceTime } from 'rxjs/operators';

//! services
import { AppointmentService } from '../../../service/admin/appointment.service';
import { ClinicService } from '../../../service/admin/clinic.service';
import { ClinicScheduleService } from '../../../service/admin/clinic-schedule.service';
import { UserService } from '../../../service/admin/user.service';
import { PatientRegistrationService } from '../../../service/admin/patient-registration.service';

import { ApiService } from '../../../service/admin/api.service';
import { WorkContextService } from '../../../service/admin/work-context.service';
import { WorkContextBarComponent } from '../../shared/work-context-bar/work-context-bar.component';
import { ToasterService } from '../../../service/common/toaster.service';
import {
  DELIVERY_TYPE_OPTIONS,
  DeliveryType,
  deliveryTypeLabel,
  matchesFlowFilter,
  resolveDeliveryType,
} from '../../../utils/delivery-type.util';

@Component({
  selector: 'app-appointment-list',
  standalone: true,
  imports: [CommonModule, ToastModule, ConfirmDialogModule, FormsModule, DropdownModule, AutoCompleteModule, DialogModule, WorkContextBarComponent],
  providers: [ConfirmationService, AppointmentService, UserService, ClinicService, ApiService],
  templateUrl: './appointment-list.component.html',
  styleUrl: './appointment-list.component.scss'
})
export class AppointmentListComponent implements OnInit, OnDestroy {
  searchSubject: Subject<string> = new Subject<string>();
  private searchSubscription!: Subscription;
  public checked: boolean = false;
  public showPassword = false;

  selectedPatient: any;
  public appointmentList: any[] = [];
  public patientList: any[] = [];
  public patientSuggestions: any[] = [];
  public cardList: any[] = [];
  public clinicList: any[] = [];
  public doctorsList: any[] = [];

  public isEditing: boolean = false;
  public showForm: boolean = false;
  public isSubmitting: boolean = false;
  public searchTerm: string = '';
  public statusFilter: string = 'all';
  public flowFilter: string = 'all';
  public dateFilter: string = '';
  public currentPage: number = 1;
  public pageSize: number = 10;
  public readonly pageSizeOptions: number[] = [10, 20, 50];

  readonly flowFilterOptions = [
    { label: 'All Flows', value: 'all' },
    { label: 'Medicine Collection', value: 'medicine' },
    { label: 'Normal', value: 'normal' },
    { label: 'Home Delivery', value: 'home_delivery' },
    { label: 'Domestic', value: 'domestic' },
    { label: 'International', value: 'international' }];

  public appointment: any = {
    id: 0,
    clinic_id: 0,
    doctor_id: 0,
    patient_id: 0,
    appointment_type: '',
    appointment_date: '',
    start_time: '',
    end_time: '',
    status: '',
    notes: '',
    medicine_collection: false,
    delivery_type: null,
    is_courier: false
  }

  readonly deliveryTypeOptions = DELIVERY_TYPE_OPTIONS;

  public status = [
    {
      label: 'Scheduled',
      value: 'scheduled'
    },
    {
      label: 'Checked In',
      value: 'checked in'
    },
    {
      label: 'In Progress',
      value: 'in progress'
    },
    {
      label: 'Medicine',
      value: 'medicine'
    },
    {
      label: 'Completed',
      value: 'completed'
    },
    {
      label: 'Cancelled',
      value: 'cancelled'
    }];

  public appointmentType: any = [
    { label: 'Consultation', value: 'consultation' },
    { label: 'Medicine Collection', value: 'medicine collection' }]

  public timeSlots: any[] = [];
  public selectedSlot: any;
  selectedDate: Date = new Date();
  private intervalId: any;

  private apiUrl: string | undefined;
  private contextSubscription = new Subscription();

  constructor(
      private toaster: ToasterService, private confirmationService: ConfirmationService,
      private appointmentService: AppointmentService,
      private userService: UserService,
      private clinicService: ClinicService,
      private clinicScheduleService: ClinicScheduleService,
      private patientRegistrationService: PatientRegistrationService,
      private http: HttpClient, 
      private cdr: ChangeDetectorRef,
      private apiService: ApiService,
      private workContext: WorkContextService
    ) {
      this.apiUrl = this.apiService.getBaseUrl();
  }

  private getAuthHeaders() {
    const token = localStorage.getItem('jwtToken');
    return {
      headers: new HttpHeaders({
        'Authorization' : `Bearer ${token}`
      })
    }
  }

  ngOnInit() {
    this.searchSubscription = this.searchSubject.pipe(
      debounceTime(800),
      distinctUntilChanged()
    ).subscribe(() => {
      this.currentPage = 1;
      this.loadAllDataAndAppointments();
    });

    this.contextSubscription.add(this.workContext.ensureOptionsLoaded().subscribe());

    this.contextSubscription.add(
      this.workContext.context$
        .pipe(distinctUntilChanged((a, b) => a.clinicId === b.clinicId && a.doctorId === b.doctorId))
        .subscribe(() => {
          this.onFiltersChanged();
          if (this.showForm) {
            if (!this.isEditing) {
              this.applyWorkContext();
            }
            this.generateTimeSlots();
          }
        }),
    );

    this.loadAllDataAndAppointments();
    this.clinicScheduleService.ensureClinicsLoaded().subscribe(() => this.generateTimeSlots());

    // ✅ Auto-refresh slots every minute
    this.intervalId = setInterval(() => {
      this.generateTimeSlots();
    }, 60 * 1000);
  }

  private applyWorkContext(): void {
    const clinicId = this.workContext.getClinicId();
    const doctorId = this.workContext.getDoctorId();
    if (clinicId != null) {
      this.appointment.clinic_id = clinicId;
    }
    if (doctorId != null) {
      this.appointment.doctor_id = doctorId;
    }
  }

  loadAllDataAndAppointments() {
    this.getClinic();
    this.getDoctors();
    this.getPatient();
    this.getAppointments();
  }

  formatStatusLabel(status: string): string {
    if (!status) return 'Unknown';
    return status
      .toLowerCase()
      .split(' ')
      .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
      .join(' ');
  }

  getStatusClass(status: string): string {
    switch ((status || '').toLowerCase()) {
      case 'scheduled':
        return 'status-scheduled';
      case 'checked in':
        return 'status-checked-in';
      case 'in progress':
        return 'status-in-progress';
      case 'medicine':
        return 'status-medicine';
      case 'completed':
        return 'status-completed';
      case 'cancelled':
        return 'status-cancelled';
      default:
        return 'status-default';
    }
  }


  getAppointments() {
    this.appointmentService.getAppointment().subscribe((res: any) => {
      this.appointmentList = (res.data || []).map((appointment: any) => {
        const patient = appointment.patient;
        const clinic = appointment.clinic;
        const doctor = appointment.doctor;
        const clinicFromList = this.clinicList.find(c => Number(c.value) === Number(appointment.clinic_id));
        const doctorFromList = this.doctorsList.find(d => Number(d.value) === Number(appointment.doctor_id));

        return {
          ...appointment,
          appointment_id: appointment.id,
          start_time: appointment.start_time,
          patient_name: this.formatPatientName(patient),
          clinic_name: clinic?.name || clinicFromList?.label || '',
          doctor_name: doctor?.name || doctorFromList?.label || '',
          status: (appointment.status || '').toLowerCase(),
          status_label: this.formatStatusLabel(appointment.status),
        };
      });
    }, () => {
      this.toaster.error('Error fetching appointment list');
    });
  }

  private formatPatientName(patient: any): string {
    if (!patient) {
      return '';
    }

    const salutation = (patient.salutation || '').trim();
    const fullName = (patient.full_name || '').trim();
    return [salutation, fullName].filter(Boolean).join(' ');
  }

  getPatient(callback?: () => void) {
    this.patientRegistrationService.getPatient().subscribe((res: any) => {
      this.patientList = res.data || [];
      this.patientSuggestions = this.patientList;
      if (callback) callback();
    },() => {
      this.patientList = [];
      this.patientSuggestions = [];
      if (callback) callback();
    });
  }

  getCards() {
    this.patientRegistrationService.getCards().subscribe({
      next: (res: any) => {
        console.log('Card API response:', res);
        this.cardList = Array.isArray(res.data)
          ? res.data.map((card:any) => ({
              label: card.number,
              value: card.id
            }))
          : [];
      },
      error: (err) => {
        console.error('Card API error:', err);
        this.cardList = [];
      }
    });
  }

  getClinic(callback?: () => void) {
    this.clinicService.getClinic().subscribe({
      next: (res: any) => {
        this.clinicList = Array.isArray(res.data)
          ? res.data.map((clinic:any) => ({
              label: clinic.name,
              value: clinic.id
            }))
          : [];
        if (callback) callback();
      },
      error: (err) => {
        console.error('Clinic API error:', err);
        this.clinicList = [];
        if (callback) callback();
      }
    });
  }

  getDoctors(callback?: () => void) {
    this.userService.getDoctors().subscribe({
      next: (res: any) => {
        this.doctorsList = Array.isArray(res.data)
          ? res.data.map((doctor:any) => ({
              label: doctor.name,
              value: doctor.id
            }))
          : [];
        if (callback) callback();
      },
      error: (err) => {
        console.error('Doctor API error:', err);
        this.doctorsList = [];
        if (callback) callback();
      }
    });
  }

  closeForm() {
    this.clearForm();
    this.showForm = false;
    this.isSubmitting = false;
  }

  clearForm() {
    this.appointment = {
      id: 0,
      clinic_id: 0,
      doctor_id: 0,
      patient_id: 0,
      appointment_type: '',
      appointment_date: '',
      start_time: '',
      end_time: '',
      status: '',
      notes: '',
      medicine_collection: false,
      delivery_type: null,
      is_courier: false,

      patient: {
        id: 0,
        clinic_id: 0,
        card_id: 0,
        patient_number: '',
        salutation: '',
        full_name: '',
        dob: '',
        age: 0,
        contact_number: '',
        address: '',
        city: '',
        state: '',
        pincode: '',
        country: '',
        occupation: '',
        referred_by: '',
        referred_by_dr: '',
        is_free: false,
        is_active: true,
        first_visit: '',

        prescriptions: {
          id: 0,
          appointment_id: 6,
          summary: '',
          medicines: '',
          chief_complaints: '',
          diagnosis: '',
          past_history: '',
          menstrual_history: '',
          obstetric_history: '',
          respiratory_system: '',
          cardiovascular_system: '',
          central_nervous_system: '',
          per_abdomen: '',

          family_history: {
            mother: '',
            father: '',
            other: ''
          },

          others: {
            nail: '',
            mentals: '',
            stool: '',
            urine: '',
            sleep: '',
            parsp: '',
            corn: '',
            mole: '',
            hair: '',
            spine: '',
            pallor: '',
            cyanosis: '',
            clubbing: '',
            icterus: '',
            thirst: '',
            craving: '',
            aversion: '',
            dreams: '',
            thermals: '',
            warts: '',
            lm: '',
            tongue: ''
          }
        }
      }
    };
    this.isEditing = false;
    this.selectedPatient = null;
    this.selectedSlot = null;
  }

  processAppointments() {
    if (this.isSubmitting) return;

    if (!this.appointment.clinic_id || !this.appointment.doctor_id) {
      this.toaster.warn('Please select clinic and doctor.', 'Missing selection');
      return;
    }

    this.isSubmitting = true;
    if (this.appointment?.id) {
      this.updateAppointment();
    } else {
      this.addAppointment();
    }
  }

  addAppointment() {
    this.appointmentService
      .addAppointment(this.appointment)
      .subscribe({
        next: (res) => {
          this.toaster.success('Appointment Created Successfuly');
          this.isSubmitting = false;
          this.closeForm();
          this.getAppointments();
        },
        error: (err) => {
          this.isSubmitting = false;
          let detailMsg = 'Error creating appointment';
          if (err.status === 422 && err.error && err.error.errors && err.error.errors.start_time) {
            detailMsg = err.error.errors.start_time[0];
          }
          this.toaster.error(detailMsg);
        }
      });
  }

  updateAppointment() {
    this.appointmentService
      .updateAppointment(this.appointment)
      .subscribe({
        next: (res) => {
          this.toaster.success('Appointment updated Successfuly.');
          this.isSubmitting = false;
          this.closeForm();
          this.getAppointments();
        },
        error: (err) => {
          this.isSubmitting = false;
          let detailMsg = 'Error updating appointment';
          if (err.status === 422 && err.error && err.error.errors && err.error.errors.start_time) {
            detailMsg = err.error.errors.start_time[0];
          }
          this.toaster.error(detailMsg);
        }
      });
  }

  deleteAppointment(item: any) {
    // console.log('We are deleting this item', item);
    this.appointmentService.deleteAppointment(item).subscribe((res: any) => {
      this.toaster.success('Destination deleted Successfuly.');
      this.closeForm();
      this.getAppointments();
    });
  }

  startAdd() {
    this.clearForm();
    this.isEditing = false;
    this.applyWorkContext();
    this.appointment.appointment_date = new Date().toISOString().slice(0, 10);
    this.appointment.status = 'scheduled';
    this.showForm = true;
    this.clinicScheduleService.ensureClinicsLoaded().subscribe(() => this.generateTimeSlots());
  }

  startEdit(item: any) {
    this.isEditing = true;
    this.showForm = true;
    this.appointment = { ...item };
    this.appointment.status = String(this.appointment.status || '').toLowerCase();
    this.appointment.medicine_collection = !!this.appointment.medicine_collection;
    this.appointment.is_courier = !!this.appointment.is_courier;

    if (this.appointment.start_time) {
      this.appointment.start_time = this.formatTimeHM(this.appointment.start_time);
    }
    if (this.appointment.end_time) {
      this.appointment.end_time = this.formatTimeHM(this.appointment.end_time);
    }

    this.selectedPatient = {
      full_name: item.patient_name || '',
      id: item.patient_id
    };

    this.clinicScheduleService.ensureClinicsLoaded().subscribe(() => {
      this.generateTimeSlots();
      this.syncSelectedSlotFromAppointment();
    });
  }

  confirmDelete(item: any) {
    this.confirmationService.confirm({
      header: 'Delete Appointment',
      message: 'Are you sure you want to delete this appointment?',
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Delete',
      rejectLabel: 'Cancel',
      acceptButtonStyleClass: 'p-button-danger',
      accept: () => this.deleteAppointment(item)
    });
  }

  setMedicineCollection(active: boolean) {
    this.appointment.medicine_collection = !!active;
    if (this.appointment.medicine_collection) {
      this.appointment.delivery_type = null;
      this.appointment.is_courier = false;
    }
  }

  isDeliveryTypeActive(type: DeliveryType): boolean {
    return resolveDeliveryType(this.appointment) === type;
  }

  setDeliveryType(type: DeliveryType) {
    const current = resolveDeliveryType(this.appointment);
    const next: DeliveryType | null = current === type ? null : type;
    this.appointment.delivery_type = next;
    this.appointment.is_courier = !!next;
    if (next) {
      this.appointment.medicine_collection = false;
    }
  }

  getDeliveryTypeLabel(item: any): string {
    return deliveryTypeLabel(resolveDeliveryType(item));
  }

  getPatientInitial(name: string): string {
    const parts = (name || '').trim().split(/\s+/).filter(Boolean);
    if (!parts.length) {
      return '?';
    }
    return parts[0].charAt(0).toUpperCase();
  }

  getFlowLabel(item: any): string {
    if (item?.medicine_collection) {
      return 'Medicine Collection';
    }
    const delivery = resolveDeliveryType(item);
    if (delivery) {
      return deliveryTypeLabel(delivery);
    }
    return 'Normal';
  }

  getFlowClass(item: any): string {
    if (item?.medicine_collection) {
      return 'flow-medicine';
    }
    if (resolveDeliveryType(item)) {
      return 'flow-courier';
    }
    return 'flow-normal';
  }

  get filteredAppointments(): any[] {
    const search = (this.searchTerm || '').trim().toLowerCase();
    const filter = (this.statusFilter || 'all').toLowerCase();
    const flow = (this.flowFilter || 'all').toLowerCase();
    const date = (this.dateFilter || '').trim();
    const contextClinicId = this.workContext.getClinicId();
    const contextDoctorId = this.workContext.getDoctorId();

    return (this.appointmentList || []).filter((item: any) => {
      if (contextClinicId != null && Number(item.clinic_id) !== contextClinicId) {
        return false;
      }
      if (contextDoctorId != null && Number(item.doctor_id) !== contextDoctorId) {
        return false;
      }

      const matchesStatus = filter === 'all' || (item.status || '').toLowerCase() === filter;
      if (!matchesStatus) return false;

      if (!matchesFlowFilter(item, flow as any)) {
        return false;
      }

      const matchesDate = !date || String(item.appointment_date || '') === date;
      if (!matchesDate) return false;

      if (!search) return true;

      const haystack = [
        item.patient_name,
        item.clinic_name,
        item.doctor_name,
        item.status_label,
        item.appointment_date,
        item.start_time
      ].join(' ').toLowerCase();

      return haystack.includes(search);
    });
  }

  get totalPages(): number {
    return Math.max(1, Math.ceil(this.filteredAppointments.length / this.pageSize));
  }

  get paginatedAppointments(): any[] {
    const safePage = Math.min(this.currentPage, this.totalPages);
    if (safePage !== this.currentPage) {
      this.currentPage = safePage;
    }
    const start = (safePage - 1) * this.pageSize;
    return this.filteredAppointments.slice(start, start + this.pageSize);
  }

  get pageNumbers(): number[] {
    return Array.from({ length: this.totalPages }, (_, i) => i + 1);
  }

  get visiblePageNumbers(): number[] {
    const radius = 2;
    const start = Math.max(1, this.currentPage - radius);
    const end = Math.min(this.totalPages, this.currentPage + radius);
    return Array.from({ length: end - start + 1 }, (_, i) => start + i);
  }

  onFiltersChanged() {
    this.searchSubject.next(this.searchTerm);
  }

  onPageSizeChange() {
    this.currentPage = 1;
  }

  goToPage(page: number) {
    if (page < 1 || page > this.totalPages) return;
    this.currentPage = page;
  }

  goToPreviousPage() {
    this.goToPage(this.currentPage - 1);
  }

  goToNextPage() {
    this.goToPage(this.currentPage + 1);
  }

  clearFilters() {
    this.searchTerm = '';
    this.statusFilter = 'all';
    this.flowFilter = 'all';
    this.dateFilter = '';
    this.currentPage = 1;
  }

  to12Hour(time: string): string {
    const normalized = this.formatTimeHM(time);
    const match = normalized.match(/^(\d{1,2}):(\d{2})$/);
    if (!match) return normalized;
    let hours = Number(match[1]);
    const minutes = match[2];
    const suffix = hours >= 12 ? 'PM' : 'AM';
    hours = hours % 12 || 12;
    return `${hours}:${minutes} ${suffix}`;
  }

  onPatientSelect(event: any) {
    this.appointment.patient_id = event.value.id || event.value.patient_id;
    this.selectedPatient = event.value;
  }

  searchPatients(event: any) {
    const query = event.query;
    this.http.get<any>(this.apiUrl + '/patient/search', { params: { q: query }, ...this.getAuthHeaders()})
      .subscribe(res => {
        if (!res.data || res.data.length === 0) {
          this.patientSuggestions = [];
          this.toaster.add({
            severity: 'info',
            summary: 'No patients found',
            detail: `No patients found for "${query}".`
          });
        } else {
          this.patientSuggestions = res.data.map((p: any) => ({
            ...p,
            full_name: `${p.full_name} (${p.patient_number})`
          }));
        }
        // Stop spinner and update view
        this.cdr.detectChanges();
      }, error => {
        this.patientSuggestions = [];
        this.toaster.info('No patients found.', 'Info');
        this.cdr.detectChanges();
      });
  }

  //! ========================= Time Slots Code ================================
  ngOnDestroy() {
    this.contextSubscription.unsubscribe();
    if (this.searchSubscription) {
      this.searchSubscription.unsubscribe();
    }
    // clear interval when component is destroyed
    if (this.intervalId) {
      clearInterval(this.intervalId);
    }
  }

  // called whenever user changes the date
  onDateChange(date: string) {
    this.appointment.appointment_date = date;
    this.generateTimeSlots();
  }

  onClinicChange() {
    this.selectedSlot = null;
    this.generateTimeSlots();
  }

  get slotScheduleNote(): string {
    return this.clinicScheduleService.getSlotsSourceLabel(
      this.resolveClinicIdForSlots(),
      this.appointment?.appointment_date,
    );
  }

  generateTimeSlots() {
    const date = this.appointment?.appointment_date;
    if (!date) {
      this.timeSlots = [];
      return;
    }

    const clinicId = this.resolveClinicIdForSlots();
    this.timeSlots = this.clinicScheduleService.generateTimeSlots(clinicId, date);
    this.syncSelectedSlotFromAppointment();
  }

  private resolveClinicIdForSlots(): number | null {
    const fromAppointment = Number(this.appointment?.clinic_id);
    if (Number.isFinite(fromAppointment) && fromAppointment > 0) {
      return fromAppointment;
    }
    return this.workContext.getClinicId();
  }

  private syncSelectedSlotFromAppointment(): void {
    if (!this.appointment?.start_time) {
      return;
    }

    const normalizedStart = this.formatTimeHM(this.appointment.start_time);
    const normalizedEnd = this.formatTimeHM(this.appointment.end_time);
    this.selectedSlot = this.timeSlots.find(
      (slot) => slot.start === normalizedStart && slot.end === normalizedEnd,
    ) ?? null;
  }

  formatTime(hour: number): string {
    return `${hour.toString().padStart(2, '0')}:00`;
  }

  /**
   * Normalize various time inputs to `HH:MM`.
   * - "HH:MM:SS" -> "HH:MM"
   * - "HH:MM" -> ensures leading zero
   * - numeric hour (e.g. 9) -> "09:00"
   * - ISO datetime -> extracts local hours:minutes
   */
  formatTimeHM(time: any): string {
    if (time == null) return '';
    const s = String(time).trim();

    // hh:mm:ss -> hh:mm
    const m1 = s.match(/^(\d{1,2}):(\d{2}):(\d{2})$/);
    if (m1) return m1[1].padStart(2, '0') + ':' + m1[2];

    // hh:mm -> normalize leading zero
    const m2 = s.match(/^(\d{1,2}):(\d{2})$/);
    if (m2) return m2[1].padStart(2, '0') + ':' + m2[2];

    // integer hour (e.g., 9 or "9") -> HH:00
    if (/^\d+$/.test(s)) return s.padStart(2, '0') + ':00';

    // try ISO/date parse
    const d = new Date(s);
    if (!isNaN(d.getTime())) {
      const hh = String(d.getHours()).padStart(2, '0');
      const mm = String(d.getMinutes()).padStart(2, '0');
      return `${hh}:${mm}`;
    }

    // fallback: if ends with :00, take first 5 chars
    if (s.length >= 5 && s.endsWith(':00')) return s.slice(0, 5);

    return s;
  }

  formatAmPm(hour: number): string {
    const suffix = hour >= 12 ? "PM" : "AM";
    const displayHour = ((hour + 11) % 12 + 1);
    return `${displayHour}:00 ${suffix}`;
  }

  selectSlot(slot: any) {
    if (slot.disabled) return;

    this.selectedSlot = slot;
    this.appointment.start_time = slot.start;
    this.appointment.end_time = slot.end;
  }
}
